// src/pages/Feedback/Feedback.jsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { submitFeedback } from '../../services/feedbackService';
import styles from './Feedback.module.css';

const FEEDBACK_TYPES = [
    { value: 'bug', label: '🐞 Bug', emoji: '🐞' },
    { value: 'feature_idea', label: '💡 Feature idea', emoji: '💡' },
    { value: 'coin_issue', label: '🪙 Coin issue', emoji: '🪙' },
    { value: 'quiz_question_issue', label: '🎬 Challenge question issue', emoji: '🎬' },
    { value: 'collection_idea', label: '💎 Collection idea', emoji: '💎' },
    { value: 'something_confusing', label: '😕 Something confusing', emoji: '😕' },
    { value: 'general_feedback', label: '❤️ General feedback', emoji: '❤️' },
];

const MIN_MESSAGE_CHARS = 5;
const MAX_MESSAGE_CHARS = 3000;
// Mirrors the backend rule in routes/feedback.js: at least 10 letters/numbers
// and at least one letter, so the user hears about it before submitting.
const MIN_MEANINGFUL_CHARS = 10;

function getMessageError(message) {
    const normalized = message.trim();
    if (normalized.length < MIN_MESSAGE_CHARS) {
        return `Message must be at least ${MIN_MESSAGE_CHARS} characters`;
    }
    if (normalized.length > MAX_MESSAGE_CHARS) {
        return `Message must be ${MAX_MESSAGE_CHARS} characters or fewer`;
    }
    const lettersAndNumbers = (normalized.match(/[a-z0-9]/gi) || []).length;
    const hasLetter = /[a-z]/i.test(normalized);
    if (lettersAndNumbers < MIN_MEANINGFUL_CHARS || !hasLetter) {
        return 'Please add a little more detail to your feedback (at least 10 letters or numbers).';
    }
    return '';
}

function Feedback() {
    const { user } = useAuth();

    const [type, setType] = useState('');
    const [message, setMessage] = useState('');
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        // Reset success/error after 5 seconds
        if (success || error) {
            const timer = setTimeout(() => {
                setSuccess(false);
                setError('');
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [success, error]);

    async function handleSubmit(e) {
        e.preventDefault();
        setError('');
        setSuccess(false);

        if (!type) {
            setError('Please select a feedback type');
            return;
        }

        const messageError = getMessageError(message);
        if (messageError) {
            setError(messageError);
            return;
        }

        setLoading(true);

        try {
            // Capture browser metadata
            const metadata = {
                pageUrl: window.location.href,
                userAgent: navigator.userAgent,
                viewportWidth: window.innerWidth,
                viewportHeight: window.innerHeight,
                platform: navigator.platform,
                referrer: document.referrer,
            };

            await submitFeedback({
                type,
                message: message.trim(),
                email: email.trim() || undefined,
                ...metadata,
            });

            setSuccess(true);
            setType('');
            setMessage('');
            setEmail('');
        } catch (err) {
            setError(err.message || 'Something went wrong sending your feedback. Please try again.');
        } finally {
            setLoading(false);
        }
    }

    return (
        <div className={styles.feedbackPage}>
            <div className={styles.feedbackContainer}>
                <div className={styles.feedbackHeader}>
                    <h1>Help shape Aluvo</h1>
                    <p className={styles.feedbackSubheading}>
                        Aluvo is still early, and your feedback helps us improve the experience.
                        Tell us what feels confusing, what broke, what you want added, or what would
                        make earning coins more fun.
                    </p>
                </div>

                {success && (
                    <div className={styles.feedbackSuccess}>
                        <span className={styles.successIcon}>✓</span>
                        Thanks — your feedback helps build Aluvo.
                    </div>
                )}

                {error && (
                    <div className={styles.feedbackError}>
                        {error}
                    </div>
                )}

                <form onSubmit={handleSubmit} className={styles.feedbackForm}>
                    <div className={styles.formGroup}>
                        <label htmlFor="type" className={styles.formLabel}>
                            Feedback Type <span className={styles.required}>*</span>
                        </label>
                        <select
                            id="type"
                            value={type}
                            onChange={(e) => setType(e.target.value)}
                            className={styles.formSelect}
                            disabled={loading}
                            required
                        >
                            <option value="">Select a type...</option>
                            {FEEDBACK_TYPES.map((ft) => (
                                <option key={ft.value} value={ft.value}>
                                    {ft.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className={styles.formGroup}>
                        <label htmlFor="message" className={styles.formLabel}>
                            Message <span className={styles.required}>*</span>
                        </label>
                        <textarea
                            id="message"
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            className={styles.formTextarea}
                            placeholder="Tell us what happened, what felt confusing, or what you'd like to see…"
                            rows={6}
                            maxLength={MAX_MESSAGE_CHARS}
                            disabled={loading}
                            required
                        />
                        <div className={styles.charCount}>
                            {message.length} / {MAX_MESSAGE_CHARS}
                        </div>
                    </div>

                    {!user && (
                        <div className={styles.formGroup}>
                            <label htmlFor="email" className={styles.formLabel}>
                                Email, if you want a reply
                            </label>
                            <input
                                type="email"
                                id="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className={styles.formInput}
                                placeholder="your@email.com"
                                disabled={loading}
                            />
                        </div>
                    )}

                    <button
                        type="submit"
                        className={styles.feedbackSubmitBtn}
                        disabled={loading || !type || Boolean(getMessageError(message))}
                    >
                        {loading ? 'Sending...' : 'Send Feedback'}
                    </button>
                </form>
            </div>
        </div>
    );
}

export default Feedback;